import {
  Drawer,
  Stack,
  Box,
  useTheme,
  Button,
  IconButton,
  Tooltip,
  ButtonBase,
  ClickAwayListener,
  MenuItem,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import SideNavSection from "./SideNavSection";
import { usePathname } from "hooks/use-pathname";
import React, { useEffect, useMemo, useRef, useState } from "react";
import inkfree_collasped_logo from "assets/images/inkfree_collasped_logo.svg";
import side_nav_inkfree_logo from "assets/images/side_nav_inkfree_logo.svg";
import history from "utils/history";
import { hasAccessTo, useAuthRouteRedirection } from "../../Config";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  selectSideNavCollpased,
  selectTheme,
  updateSideNavCollpased,
} from "redux/preferencesSlice";
import ArrowLeft from "assets/icons/arrow_left.svg";
import sideNavStyles from "layouts/DrawerLayout/components/SideNav.Styles";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import viewpaths from "global/constants/ViewPathConstants";
import {
  setReduxSelectedParticipant,
  setReduxSignRequest,
} from "redux/appSlice";
import { addSigner } from "screens/SignRequest/SignRequestHelper";
import { selectEmail } from "redux/authSlice";
import { RootState } from "utils/store";
import CustomDropdownButton from "global/components/CustomDropdownButton/CustomDropdownButton";
import {
  BulkSendIcon,
  MeAndOthersIcon,
  OnlySignerIcon,
} from "global/components/RequestTypeIcons";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { isOfferActive } from "helpers/methods";
import strings from "global/constants/StringConstants";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import { nav } from "theme/colors";
import { tourAttr } from "tour";

const SIDE_NAV_WIDTH = 280;
const SIDE_NAV_COLLAPSED_WIDTH = 70;

const useCssVars = (): Record<string, string> => {
  const themePreference = useAppSelector(selectTheme);
  const theme = useTheme();

  return useMemo((): Record<string, string> => {
    // The nav shell reads identically in both theme preferences — its surface is
    // always white — so both branches pull the same `nav` tokens and only the
    // scrollbar/divider chrome differs.
    if (themePreference === "dark") {
      return {
        "--nav-bg": theme.palette.common.white,
        "--nav-color": theme.palette.text.primary,
        "--nav-divider-color": theme.palette.neutral[800],
        "--nav-border-color": nav.border,
        "--nav-logo-border": theme.palette.neutral[700],
        "--nav-item-color": nav.itemText,
        "--nav-item-hover-bg": nav.hoverBg,
        "--nav-item-hover-color": nav.activeText,
        "--nav-item-active-bg": nav.activeBg,
        "--nav-item-disabled-color": theme.palette.text.disabled,
        "--nav-item-icon-color": nav.itemText,
        "--nav-item-icon-active-color": nav.activeIcon,
        "--nav-item-icon-disabled-color": theme.palette.text.disabled,
        "--nav-item-chevron-color": nav.itemText,
        "--nav-scrollbar-color": theme.palette.neutral[400],
        "--nav-item-active-color": nav.activeText,
        "--nav-section-title-color": theme.palette.text.primary,
        "--nav-footer-color": nav.footerText,
        // Sub-level rows share the top-level active treatment (tinted row,
        // ink label) and are told apart by the rail marker alone.
        "--nav-item-active-bar": nav.activeBar,
      };
    } else {
      return {
        "--nav-bg": theme.palette.common.white,
        "--nav-color": theme.palette.text.primary,
        "--nav-divider-color": theme.palette.divider,
        "--nav-border-color": nav.border,
        "--nav-logo-border": theme.palette.neutral[100],
        "--nav-section-title-color": theme.palette.text.primary,
        "--nav-item-color": nav.itemText,
        "--nav-item-hover-bg": nav.hoverBg,
        "--nav-item-active-bg": nav.activeBg,
        "--nav-item-active-color": nav.activeText,
        "--nav-item-disabled-color": theme.palette.text.disabled,
        "--nav-item-icon-color": nav.itemText,
        "--nav-item-icon-active-color": nav.activeIcon,
        "--nav-item-icon-disabled-color": theme.palette.text.disabled,
        "--nav-item-chevron-color": nav.itemText,
        "--nav-scrollbar-color": theme.palette.neutral[600],
        "--nav-footer-color": nav.footerText,
        // Sub-level rows share the top-level active treatment (tinted row,
        // ink label) and are told apart by the rail marker alone.
        "--nav-item-active-bar": nav.activeBar,
      };
    }
  }, [theme]);
};

interface CustomProps {
  sections: any;
}

const SideNav = (props: CustomProps) => {
  const theme = useTheme();
  const classes = sideNavStyles;
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const cssVars = useCssVars();
  const authRoute = useAuthRouteRedirection();
  const themePreference = useAppSelector(selectTheme);
  const sideNavCollpased = useAppSelector(selectSideNavCollpased);
  const isDarkTheme = themePreference === "dark";
  const userEmail: string = useAppSelector(selectEmail);
  const { userName } = useAppSelector((state: RootState) => state.auth);

  const bulkSendState = useFeatureState(FeatureKeys.ENVELOPE_BULK_SEND);
  const isBulkSendAvailable =
    bulkSendState.status === strings.FEATURE_STATUS_AVAILABLE;
  const bulkSendTooltip = isBulkSendAvailable
    ? ""
    : bulkSendState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((bulkSendState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : strings.FEATURE_GATE_UPSELL_TOOLTIP;

  // Collapsed rail: the create-request options have no caret to click, so the
  // icon reveals them on hover instead. A short close delay lets the pointer
  // cross the gap between the button and the flyout without it vanishing.
  const [createAnchor, setCreateAnchor] = useState<HTMLElement | null>(null);
  const createCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const createTriggerRef = useRef<HTMLButtonElement | null>(null);
  const createMenuRef = useRef<HTMLDivElement | null>(null);
  const focusCreateMenuRef = useRef(false);
  const suppressCreateOpenRef = useRef(false);
  const createMenuId = "side-nav-create-request-menu";

  const cancelCreateClose = () => {
    if (createCloseTimer.current) {
      clearTimeout(createCloseTimer.current);
      createCloseTimer.current = null;
    }
  };

  const openCreateFlyout = (element: HTMLElement) => {
    cancelCreateClose();
    setCreateAnchor(element);
  };

  const scheduleCreateClose = () => {
    cancelCreateClose();
    createCloseTimer.current = setTimeout(() => setCreateAnchor(null), 160);
  };

  const closeCreateFlyout = (returnFocus = false) => {
    cancelCreateClose();
    focusCreateMenuRef.current = false;
    setCreateAnchor(null);
    if (returnFocus && createTriggerRef.current) {
      suppressCreateOpenRef.current = true;
      createTriggerRef.current.focus();
    }
  };

  const getCreateMenuItems = () =>
    Array.from(
      createMenuRef.current?.querySelectorAll<HTMLElement>(
        '[role="menuitem"]:not([aria-disabled="true"])',
      ) ?? [],
    );

  const handleCreateMenuKeyDown = (event: React.KeyboardEvent) => {
    const items = getCreateMenuItems();
    if (event.key === "Escape" || event.key === "Tab") {
      event.preventDefault();
      closeCreateFlyout(true);
      return;
    }
    if (!items.length) return;
    const current = items.indexOf(document.activeElement as HTMLElement);
    if (event.key === "ArrowDown") {
      event.preventDefault();
      items[(current + 1) % items.length].focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      items[(current - 1 + items.length) % items.length].focus();
    } else if (event.key === "Home") {
      event.preventDefault();
      items[0].focus();
    } else if (event.key === "End") {
      event.preventDefault();
      items[items.length - 1].focus();
    }
  };

  useEffect(() => {
    if (!createAnchor || !focusCreateMenuRef.current) return;
    const items = Array.from(
      createMenuRef.current?.querySelectorAll<HTMLElement>(
        '[role="menuitem"]:not([aria-disabled="true"])',
      ) ?? [],
    );
    items[0]?.focus();
    focusCreateMenuRef.current = false;
  }, [createAnchor]);

  useEffect(() => cancelCreateClose, []);

  const toggleNav = () => {
    dispatch(updateSideNavCollpased(!sideNavCollpased));
  };

  const handleRequestTypeSelect = (type: string) => {
    if (type === "signer") {
      const selfSigner = {
        id: 0,
        name: userName,
        email: userEmail,
        order: 0,
        color: "",
        cc: false,
      };
      addSigner(selfSigner, [], dispatch, false);
      dispatch(setReduxSelectedParticipant(selfSigner));
      history.push(viewpaths.createSelfSignRequestPath);
      return
    }

    history.push(`${viewpaths.createSignRequestsViewPath}?type=${type}`);
  };

  /** Shared by the expanded dropdown and the collapsed rail's hover flyout. */
  const createRequestMenuItems = [
    {
      icon: <OnlySignerIcon sx={{ fontSize: 20, color: "text.secondary" }} />,
      label: "I am the only signer",
      onClick: () => handleRequestTypeSelect("signer"),
    },
    {
      icon: <MeAndOthersIcon sx={{ fontSize: 20, color: "text.secondary" }} />,
      label: "Me and others",
      onClick: () => handleRequestTypeSelect("others"),
    },
    {
      icon: (
        <BulkSendIcon
          sx={{
            fontSize: 20,
            color: isBulkSendAvailable ? "text.secondary" : "text.disabled",
          }}
        />
      ),
      label: "Bulk send",
      disabled: !isBulkSendAvailable,
      tooltip: !isBulkSendAvailable ? bulkSendTooltip : undefined,
      endIcon: !isBulkSendAvailable ? (
        <LockOutlinedIcon sx={{ fontSize: 16, color: "neutral.400" }} />
      ) : undefined,
      onClick: () => {
        if (!isBulkSendAvailable) return;
        history.push(viewpaths.bulkSendViewPath);
      },
    },
  ];

  return (
    <Box>
      <Drawer
        anchor="left"
        open
        PaperProps={{
          sx: {
            ...cssVars,
            backgroundColor: "var(--nav-bg)",
            borderRightColor: "var(--nav-border-color)",
            borderRightStyle: "solid",
            borderRightWidth: 1,
            color: "var(--nav-color)",
            width: sideNavCollpased ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH,
            overflowX: "hidden",
            transition: "width 0.3s ease-in-out",
            top: isOfferActive() ? 30 : 0,
          },
        }}
        variant="permanent"
      >
        {/* The Drawer paper already draws the 1px --nav-border-color edge. */}
        <Stack sx={{ height: "100%" }}>
          <Stack spacing={4} sx={{ px: sideNavCollpased ? 2 : 0, pt: 3 }}>
            <Box
              component="button"
              type="button"
              aria-label="InkFree home"
              onClick={() => history.push(authRoute)}
              sx={{
                ...(sideNavCollpased
                  ? classes.inkfreeLogoBoxCollapsed
                  : classes.inkfreeLogoBox),
                border: "none",
                background: "transparent",
                cursor: "pointer",
                position: "relative",
                "&:focus-visible": { outline: "none" },
                "&:focus-visible::after": {
                  content: '""',
                  position: "absolute",
                  inset: sideNavCollpased ? "0px -6px" : "0px 12px",
                  borderRadius: "8px",
                  outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 0,
                  pointerEvents: "none",
                },
              }}
            >
              {!sideNavCollpased ? (
                <img
                  src={side_nav_inkfree_logo}
                  alt=""
                  aria-hidden="true"
                  width="150px"
                />
              ) : (
                <img
                  src={inkfree_collasped_logo}
                  alt=""
                  aria-hidden="true"
                  width="25px"
                />
              )}
            </Box>
            {hasAccessTo(strings.resources.sign, strings.sendPermission) && (
              <Stack
                px={sideNavCollpased ? 0 : 2}
                {...tourAttr("shell.createRequest")}
              >
                {!sideNavCollpased ? (
                  <CustomDropdownButton
                    handleMainClick={() => handleRequestTypeSelect("others")}
                    startIcon={<AddCircleOutlineRoundedIcon color="inherit" />}
                    buttonLabel="Create Request"
                    menuItems={createRequestMenuItems}
                  />
                ) : (
                  <Tooltip
                    title={"Create Request"}
                    placement="right"
                    arrow
                    // The flyout takes over once it is open, so the two don't
                    // stack on top of each other.
                    disableHoverListener={Boolean(createAnchor)}
                  >
                    {/* Collapsed rail keeps the primary-button fill of the
                        expanded "Create Request" button — only the label drops.
                        With no caret to click, the options open on hover. */}
                    <ButtonBase
                      ref={createTriggerRef}
                      aria-label="Create Request"
                      aria-haspopup="menu"
                      aria-expanded={Boolean(createAnchor)}
                      aria-controls={createAnchor ? createMenuId : undefined}
                      onKeyDown={(event) => {
                        if (
                          event.key === "ArrowDown" ||
                          event.key === "ArrowRight"
                        ) {
                          event.preventDefault();
                          focusCreateMenuRef.current = true;
                          openCreateFlyout(event.currentTarget);
                        } else if (event.key === "Escape") {
                          closeCreateFlyout();
                        }
                      }}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        alignSelf: "center",
                        width: 38,
                        height: 38,
                        borderRadius: "8px",
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        transition: "background-color 0.15s",
                        "&:hover": {
                          backgroundColor: "primary.dark",
                        },
                        "&:focus-visible": {
                          outline: (theme) =>
                            `2px solid ${theme.palette.primary.main}`,
                          outlineOffset: "2px",
                        },
                      }}
                      onMouseEnter={(event) =>
                        openCreateFlyout(event.currentTarget)
                      }
                      onMouseLeave={scheduleCreateClose}
                      onFocus={(event) => {
                        if (suppressCreateOpenRef.current) {
                          suppressCreateOpenRef.current = false;
                          return;
                        }
                        openCreateFlyout(event.currentTarget);
                      }}
                      onBlur={(event) => {
                        const next = event.relatedTarget as Node | null;
                        if (next && createMenuRef.current?.contains(next)) return;
                        scheduleCreateClose();
                      }}
                      onClick={() => {
                        cancelCreateClose();
                        setCreateAnchor(null);
                        dispatch(setReduxSignRequest(null));
                        history.push({
                          pathname: viewpaths.createSignRequestsViewPath,
                          search: "?type=others",
                          state: { isFileUploaded: false },
                        });
                      }}
                    >
                      <AddCircleOutlineRoundedIcon
                        sx={{ fontSize: 22, color: "inherit" }}
                      />
                    </ButtonBase>
                  </Tooltip>
                )}
                {sideNavCollpased && (
                  <Popper
                    open={Boolean(createAnchor)}
                    anchorEl={createAnchor}
                    placement="right-start"
                    // 8px stand-off matching the rail's own rhythm; the gap is
                    // bridged by the close delay, not by an invisible hit area.
                    modifiers={[
                      { name: "offset", options: { offset: [0, 8] } },
                    ]}
                    sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
                    onMouseEnter={cancelCreateClose}
                    onMouseLeave={scheduleCreateClose}
                  >
                    <ClickAwayListener onClickAway={() => setCreateAnchor(null)}>
                      <Paper
                        elevation={8}
                        id={createMenuId}
                        ref={createMenuRef}
                        role="menu"
                        aria-label="Create Request options"
                        onKeyDown={handleCreateMenuKeyDown}
                        sx={{
                          width: 220,
                          py: 0.5,
                          border: (theme) =>
                            `1px solid ${theme.palette.neutral[200]}`,
                          borderRadius: 1,
                        }}
                      >
                        {createRequestMenuItems.map((item) => {
                          const row = (
                            <MenuItem
                              key={item.label}
                              role="menuitem"
                              disabled={item.disabled}
                              onClick={() => {
                                closeCreateFlyout();
                                item.onClick();
                              }}
                              sx={{
                                px: 2,
                                minHeight: "44px",
                                "&:hover": { backgroundColor: "neutral.100" },
                                "&.Mui-focusVisible, &:focus-visible": {
                                  outline: (theme) =>
                                    `2px solid ${theme.palette.primary.main}`,
                                  outlineOffset: "-2px",
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  mr: 1.5,
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {item.icon}
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, color: "text.primary" }}
                              >
                                {item.label}
                              </Typography>
                              {item.endIcon && (
                                <Box
                                  sx={{
                                    ml: "auto",
                                    pl: 1.5,
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {item.endIcon}
                                </Box>
                              )}
                            </MenuItem>
                          );
                          return item.tooltip ? (
                            <Tooltip
                              key={item.label}
                              title={item.tooltip}
                              placement="right"
                              arrow
                            >
                              <Box
                                component="span"
                                role="none"
                                sx={{ display: "block" }}
                              >
                                {row}
                              </Box>
                            </Tooltip>
                          ) : (
                            row
                          );
                        })}
                      </Paper>
                    </ClickAwayListener>
                  </Popper>
                )}
              </Stack>
            )}
          </Stack>
          <Stack
            id="side-nav-sections"
            component="nav"
            aria-label="Main"
            spacing={2}
            sx={{
              flexGrow: 1,
              px: sideNavCollpased ? 2 : 0,
              mt: sideNavCollpased ? 2 : 4,
              transition: "padding 0.3s ease-in-out",
            }}
          >
            {props.sections.map((section: any, idx: any) => (
              <SideNavSection
                key={idx}
                items={section.items}
                subheader={section.subheader}
                isCollapsed={sideNavCollpased}
                pathname={pathname}
                action={section.action}
              />
            ))}
          </Stack>
          {sideNavCollpased ? (
            <Tooltip title="Open Sidebar" arrow placement="right">
              <IconButton
                onClick={toggleNav}
                color="primary"
                aria-label="Open sidebar"
                aria-expanded={!sideNavCollpased}
                aria-controls="side-nav-sections"
                sx={{
                  alignItems: "center",
                  borderRadius: 1,
                  display: "flex",
                  justifyContent: "center",
                  py: "25px",
                  width: "100%",
                  mb: isOfferActive() ? "30px" : "0px",
                  "&.Mui-focusVisible": {
                    outline: (theme) =>
                      `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: "-2px",
                  },
                }}
              >
                <img
                  src={ArrowLeft}
                  width={"15px"}
                  height={"15px"}
                  alt=""
                  aria-hidden="true"
                  style={{
                    transition: "transform 0.3s ease-in-out",
                    transform: sideNavCollpased
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                  }}
                />
              </IconButton>
            </Tooltip>
          ) : (
            <Box
              sx={{
                width: "100%",
                boxSizing: "border-box",
                padding: "0 16px 18px",
              }}
            >
              <ButtonBase
                onClick={toggleNav}
                aria-label="Close sidebar"
                aria-expanded={!sideNavCollpased}
                aria-controls="side-nav-sections"
                sx={{
                  alignItems: "center",
                  boxSizing: "border-box",
                  borderRadius: "10px",
                  display: "flex",
                  justifyContent: "flex-start",
                  py: "12px",
                  px: "12px",
                  width: "100%",
                  mb: isOfferActive() ? "30px" : "0px",
                  "&:hover": {
                    background: "var(--nav-item-hover-bg)",
                  },
                  "&.Mui-focusVisible": {
                    outline: (theme) =>
                      `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: "-2px",
                  },
                }}
              >
                <img
                  src={ArrowLeft}
                  width={"15px"}
                  height={"15px"}
                  alt=""
                  aria-hidden="true"
                  style={{
                    transition: "transform 0.3s ease-in-out",
                    transform: sideNavCollpased
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                  }}
                />
                {!sideNavCollpased && (
                  <Box
                    component="span"
                    sx={{
                      color: "var(--nav-footer-color)",
                      fontWeight: 600,
                      fontFamily: (theme) => theme.typography.fontFamily,
                      fontSize: "13.5px",
                      lineHeight: "20px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      marginLeft: "9px",
                    }}
                  >
                    Close Sidebar
                  </Box>
                )}
              </ButtonBase>
            </Box>
          )}
        </Stack>
      </Drawer>
    </Box>
  );
};

export default SideNav;
